import { CARD_BASE_HEIGHT, React, variantProvider } from '@/app'
import { CardBase, CardBaseProps, Icon, Image, View, Text, ProgressBar, ActivityIndicator } from '@/components'
import { APIClient } from '@/services'
import { Hurdle, Module } from '@/types'
import { formatHurdleDate, getHurdleProgressProps, useIsMobile } from '@/utils'

const ICON_SIZE = 12
const IMAGE_WIDTH = '25%'

export type HurdleCardProps = Partial<Omit<CardBaseProps, 'debugName'>> & {
  module: Module
  hurdle?: Hurdle
  hurdleId?: Hurdle['id']
  showYear?: boolean
}

export const HurdleCard = (props: HurdleCardProps) => {
  const { module, disabled, hurdle, hurdleId: _hurdleId, showYear, ...rest } = props

  const hurdleId = APIClient.Games.useLoggedOutHurdle(_hurdleId)

  const currentHurdleQuery = APIClient.Games.useHurdle(hurdleId, { enabled: !hurdle })

  const isMobile = useIsMobile()

  const data = hurdle ?? currentHurdleQuery.data
  const isLoading = hurdle ? false : currentHurdleQuery.isLoading
  const date = data?.date ?? module?.created_datetime

  const formattedDate = formatHurdleDate(date, showYear)

  const image = React.useMemo(() => {
    if (isMobile) return module?.mobile_image ?? module?.image
    return module?.web_image ?? module?.image
  }, [isMobile])

  return (
    <CardBase variants={['card:collection', disabled && 'card:disabled']} debugName='Hurdle card' {...rest} gap={null}>
      <View style={styles.imageWrapper}>
        <Image objectFit='cover' source={image} style={styles.image} />
      </View>

      <View variants={['column', 'alignStart', 'justifyStart', 'padding:2', 'fullWidth', 'flex', 'fullHeight']}>
        <View variants={['row', 'fullWidth', 'justifySpaceBetween', 'alignCenter']}>
          <View variants={['row', 'center']}>
            <Icon debugName='calendar_icon' name='calendar' size={ICON_SIZE} variants={['neutral5']} />
            <Text variants={['p4', 'color:neutral5', 'marginLeft:0.5']} text={formattedDate} />
          </View>

          <View style={styles.progressBar}>
            {isLoading ? (
              <View variants={['relative']}>
                <ActivityIndicator debugName='loading-hurdle-progress' style={styles.indicator} />
              </View>
            ) : (
              <ProgressBar {...getHurdleProgressProps(data)} />
            )}
          </View>
        </View>

        <Text variants={['h5', 'color:neutral6', 'marginTop:1']} text={module?.title} numberOfLines={3} />
      </View>
    </CardBase>
  )
}

const styles = variantProvider.createComponentStyle(theme => ({
  imageWrapper: {
    height: 'auto',
    minWidth: IMAGE_WIDTH,
    maxWidth: IMAGE_WIDTH,
    flex: 1,
    overflow: 'hidden',
    maxHeight: CARD_BASE_HEIGHT.collection,
    minHeight: CARD_BASE_HEIGHT.collection,
  },
  image: {
    width: '100%',
    height: '100%',
    minHeight: '100%',
    borderBottomLeftRadius: theme.borderRadius.small,
    borderTopLeftRadius: theme.borderRadius.small,
  },
  progressBar: {
    ...theme.presets.center,
    marginRight: theme.spacing.value(0.5),
  },
  indicator: {
    width: 24,
    height: 24,
  },
}), true)
